import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agrogestao } from '@/api/agrogestaoClient';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

export function SuporteAdmin() {
    const [busca, setBusca] = useState('');
    const [statusFiltro, setStatusFiltro] = useState('todos');

    const { data: chamados = [], isLoading } = useQuery({
        queryKey: ['suporte_admin'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('chamados')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(500);
            if (error) return [];
            return data || [];
        }
    });

    const filtered = useMemo(() => {
        return (chamados || []).filter(c => {
            if (busca && !String(c.titulo || '').toLowerCase().includes(busca.toLowerCase())) return false;
            if (statusFiltro !== 'todos' && String(c.status || '') !== statusFiltro) return false;
            return true;
        });
    }, [chamados, busca, statusFiltro]);

    const setStatus = async (id, status) => {
        try {
            await agrogestao.client.from('chamados').update({ status }).eq('id', id);
            // Optimistic update or refetch could be done here, but relying on react-query auto-refetch if invalidated or just waiting next mount
        } catch { }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <Input placeholder="Buscar chamado" value={busca} onChange={(e) => setBusca(e.target.value)} />
                <Select value={statusFiltro} onValueChange={setStatusFiltro}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        <SelectItem value="aberto">Aberto</SelectItem>
                        <SelectItem value="em_andamento">Em andamento</SelectItem>
                        <SelectItem value="resolvido">Resolvido</SelectItem>
                    </SelectContent>
                </Select>
                <Button className="bg-indigo-600 hover:bg-indigo-700">Novo chamado</Button>
            </div>
            <div className="overflow-x-auto rounded border">
                <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-stone-600 font-medium">
                        <tr className="border-b">
                            <th className="text-left py-3 px-4">Criado</th>
                            <th className="text-left py-3 px-4">Título</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Categoria</th>
                            <th className="text-left py-3 px-4">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {isLoading ? (
                            <tr><td colSpan={5} className="py-8 text-center text-stone-500">Carregando...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={5} className="py-8 text-center text-stone-500">Nenhum chamado encontrado</td></tr>
                        ) : filtered.map(c => (
                            <tr key={c.id} className="hover:bg-stone-50">
                                <td className="py-3 px-4 text-stone-600">{c.created_at ? format(new Date(c.created_at), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                <td className="py-3 px-4 font-medium">{c.titulo || '-'}</td>
                                <td className="py-3 px-4">
                                    <Badge className={
                                        c.status === 'aberto' ? 'bg-red-100 text-red-700' :
                                            c.status === 'resolvido' ? 'bg-green-100 text-green-700' :
                                                'bg-amber-100 text-amber-700'
                                    }>
                                        {c.status || 'aberto'}
                                    </Badge>
                                </td>
                                <td className="py-3 px-4">{c.categoria || '-'}</td>
                                <td className="py-3 px-4">
                                    <Select value={c.status} onValueChange={(v) => setStatus(c.id, v)}>
                                        <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="aberto">Aberto</SelectItem>
                                            <SelectItem value="em_andamento">Andamento</SelectItem>
                                            <SelectItem value="resolvido">Resolvido</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
